/**
 * voice-v8 — Geteilter Projekt-Datei-Viewer (Pre.794, #36/#62).
 *
 * Extrahiert aus ProjectsView.openFileViewer — Projekte UND Material nutzen
 * jetzt DIESELBE Detail-Ansicht (.project-sheet-overlay, z-1100 — liegt
 * korrekt ÜBER .sheet z-950): große Ansicht, Bearbeiten/Speichern, Löschen,
 * Binär-Previews (PDF/Bild/Audio).
 *
 * Sanierungen beim Verschieben (Architekt-Mandat):
 *   - Inline-`style.display`-Toggles → `hidden`-Property
 *   - .projects-btn → .btn-System (ghost/primary/danger)
 *   - kein transformContent: Inhalte werden überall ROH gezeigt — eine
 *     Ansicht-strippt/Editor-zeigt-Spaltung wäre eine Datenverlust-Falle
 *     (Faktentreue-Header beim Speichern zerstörbar).
 */

import {
  deleteProjectFile,
  getProjectFile,
  narrateProjectFile,
  saveProjectFileAsMp3,
  updateProjectFile,
  type ProjectFileType,
} from "./projects-api.js";
import { icon } from "./icons.js";
import { renderMarkdown } from "./markdown.js";
import { buildPageHeader, confirmSheet, selectSheet } from "./sheet-ui.js";
import { pushView, closeTopmost } from "./view-stack.js";
import { hoerfassungText, splitDocChapters } from "./doc-narration.js";
import { contentVersion } from "./playback-resume.js";
import { beobachteElement } from "./seiten-ton.js";

interface FileViewerOptions {
  /** Wird nach Speichern/Löschen gerufen (Liste auffrischen). */
  onChange?: () => void;
  /**
   * Beim Öffnen gleich vorlesen. (19.09.2026, Ticket 2395)
   *
   * ⚠️ Ein Versuch, keine Zusage. Der Betrachter lädt die Datei erst nach
   *    (`await`), und damit ist die Geste des Nutzers verbraucht — der
   *    Browser kann den Ton dann verweigern (Ticket 2329). Der Spieler
   *    steht in dem Fall sichtbar bereit und sagt seit pre.3031 auch,
   *    warum er schweigt.
   */
  vorlesen?: boolean;
}

/**
 * Pre.974: Geteilter Vorlese-Player. buildNarrationPlayer lebt in app.ts
 * (direkter Import wäre zirkulär), darum reicht app.ts den Builder einmalig
 * per Setter herein — derselbe Player wie Mein Leben / HD / Essenz / Projekte.
 */
export interface ViewerNarrationPlayer {
  el: HTMLElement;
  setReady: (ready: boolean) => void;
  prefetch: () => void;
  setSections: (items: Array<{ title: string; text: string }>) => void;
  /** Stoppt Audio + räumt auf (beim Modus-Wechsel und beim Schließen). */
  reset: () => void;
  /** Von aussen starten (Ticket 2395). Fehlt bei älteren Buildern. */
  play?: () => void;
  /**
   * Arbeitet der Player gerade (spielt, puffert, wartet aufs nächste Stück, steht auf Pause)? Nur „idle“ und
   * „error“ sind still. (pre.3621) Das <audio> allein sagt das nicht: zwischen zwei Stücken und zwei
   * Abschnitten meldet es `ended`, und die Engine spielt gleich danach auf demselben Element weiter.
   */
  aktiv?: () => boolean;
}
type ViewerPlayerBuilder = (opts: {
  endpoint: string;
  getBody: (voiceId: string) => Record<string, unknown> | null;
  sectionable?: boolean;
  resumeId?: string;
  resumeVersion?: string;
}) => ViewerNarrationPlayer;

let playerBuilder: ViewerPlayerBuilder | null = null;
export function setViewerPlayerBuilder(fn: ViewerPlayerBuilder): void {
  playerBuilder = fn;
}

/** Liefert den (von app.ts beim Start gesetzten) Player-Builder — z. B. für den
 * Alltag-Kapitel-Player, ohne app.ts erneut zu verdrahten. */
export function getViewerPlayerBuilder(): ViewerPlayerBuilder | null {
  return playerBuilder;
}

export function typeLabel(type: ProjectFileType): string {
  switch (type) {
    case "chapter":
      return "Kapitel";
    case "character":
      return "Personen";
    case "note":
      return "Notiz";
    case "recording":
      return "Aufnahme";
    case "transcript":
      return "Transcript";
    case "reference":
      return "Dokument";
    case "pdf":
      return "PDF";
    case "image":
      return "Bild";
    case "vocabulary":
      return "Vokabeln";
    case "other":
    default:
      return "Sonstige";
  }
}

/**
 * Pre.978: Aus einem Datei-/Upload-Namen einen schönen Anzeige-Titel machen:
 * Endung weg, Unterstriche → Leerzeichen, abschließenden Versions-/Druck-
 * Vermerk („… V66 Druck", „… v3", „… final") entfernen. Konservativ — nur
 * am ENDE und nur bekannte Vermerke, damit echte Titel nicht verstümmelt werden.
 */
export function prettyTitle(raw: string): string {
  let s = (raw ?? "")
    .replace(/\.[a-z0-9]{1,5}$/i, "")
    .replace(/_+/g, " ")
    .trim();
  s = s.replace(
    /\s+[vV]\d+\w*(\s+(druck|print|final|entwurf|draft|kopie|copy))?\s*$/i,
    "",
  );
  s = s.replace(/\s+(druck|print|final|entwurf|draft|kopie|copy)\s*$/i, "");
  return s.replace(/\s{2,}/g, " ").trim() || raw;
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Der Anzeigetitel einer Textdatei: die erste Ueberschrift, wenn es eine gibt.
 *
 * ⚠️ 15.09.2026 — Martin sah „Kapitel-01-Haltung" im Kopf: den Dateinamen,
 *    den ein Werkzeug vergeben hat, mit Bindestrichen. Die Datei selbst weiss
 *    es besser — ihre erste Zeile lautet „# Kapitel 1 — Haltung". Was der
 *    Autor als Ueberschrift geschrieben hat, ist der Titel; der Dateiname
 *    bleibt der Rueckfall.
 */
const H1_RE = /^\s{0,3}#\s+(.+?)\s*#*\s*$/m;

export function anzeigeTitel(rawTitle: string, content: string | null): string {
  const m = H1_RE.exec(content ?? "");
  const h1 = m?.[1]?.trim();
  return h1 && h1.length <= 120 ? h1 : prettyTitle(rawTitle);
}

/**
 * Der Lesetext OHNE die Zeile, die schon im Kopf steht.
 *
 * ⚠️ Nur fuer die Ansicht — der Editor und das Speichern behalten den
 *    Rohtext. Der Pruefer hat es gefunden: renderMarkdown setzt `#` als h2,
 *    eine CSS-Regel auf h1 haette nie gegriffen, und die Ueberschrift stuende
 *    doppelt.
 */
export function leseTextOhneKopfzeile(rawTitle: string, content: string): string {
  const m = H1_RE.exec(content);
  if (!m || anzeigeTitel(rawTitle, content) !== m[1]?.trim()) return content;
  return content.replace(m[0], "").replace(/^\s*\n/, "");
}

export async function openProjectFileViewer(
  projectId: string,
  fileId: string,
  opts: FileViewerOptions = {},
): Promise<void> {
  const onChange = opts.onChange ?? ((): void => undefined);
  // Pre.975: aktiver Vorlese-Player — beim Schließen/Modus-Wechsel reset()en,
  // sonst läuft das Audio weiter, obwohl das Element aus dem DOM raus ist.
  let activePlayer: ViewerNarrationPlayer | null = null;
  const teardownViewer = (): void => {
    try {
      activePlayer?.reset();
    } catch {
      /* ignore */
    }
    overlay.remove();
  };

  /*
   * ⚠️⚠️ 15.09.2026 — DER KOPF HING ALS GRUENER BLOCK, DER TEXT WAR ROH.
   *
   * Martin, mit Foto: *„Der Header-Bereich sieht schrecklich aus. Bitte
   * diese Seite komplett ueberarbeiten."*
   *
   * Der Betrachter war das letzte Modal unter den Vollseiten: ein
   * `.project-sheet` mit 24px Polster und eigenem Kopf (`.project-sheet-
   * header`), auf den die geteilte Kopf-Norm `background: var(--bg)` legt —
   * auf dem helleren Blatt ein gruener Balken. Das Projekt-Detail hatte fuer
   * genau das schon eine Ausnahme; hier fehlte sie.
   *
   * Jetzt ist es dieselbe Seite wie das Projekt-Detail: dieselbe Huelle
   * (`project-sheet-v2--full`), derselbe Kopf (`buildPageHeader` mit
   * Untertitel und ⋯), dieselbe linke Kante (`--projekt-rand`). Bearbeiten,
   * MP3 und Loeschen stehen im ⋯ — im Lesemodus liegt unter dem Text kein
   * Knopf mehr. Speichern/Abbrechen erscheinen nur beim Bearbeiten.
   */
  const overlay = document.createElement("div");
  overlay.className = "project-sheet-overlay project-sheet-overlay--full";
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) closeTopmost();
  });
  const sheet = document.createElement("div");
  sheet.className = "project-sheet project-sheet-v2 project-sheet-v2--full";
  sheet.setAttribute("role", "dialog");

  const headWrap = document.createElement("header");
  headWrap.className = "project-sheet-v2-head";
  // Provisorischer Kopf, bis die Datei da ist — der Rueckweg muss sofort gehen.
  headWrap.appendChild(
    buildPageHeader("Lade …", () => closeTopmost(), { reopenMenu: false }),
  );
  sheet.appendChild(headWrap);

  const body = document.createElement("div");
  body.className = "project-sheet-v2-content project-file-body";
  sheet.appendChild(body);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);
  pushView(overlay, { onClose: teardownViewer });

  // Laufende Vorgaenge (MP3-Erzeugung) haben einen Ort — dieselbe Zeile wie
  // der Bericht auf der Projektseite.
  const laufZeile = document.createElement("p");
  laufZeile.className = "project-sheet-v2-lauf";
  laufZeile.hidden = true;
  laufZeile.setAttribute("role", "status");
  const melde = (text: string | null): void => {
    laufZeile.hidden = !text;
    laufZeile.textContent = text ?? "";
  };

  try {
    const data = await getProjectFile(projectId, fileId);
    let aktuellerText = data.content ?? "";
    const untertitel = (): string =>
      `${typeLabel(data.item.type)} · ${formatBytes(data.item.sizeBytes)}` +
      (data.item.author ? ` · von ${data.item.author}` : "");

    // Die Aktionen kennen die Funktionen erst nach dem Aufbau — sie greifen
    // ueber diese Zeiger, die weiter unten gesetzt werden.
    let bearbeiten: () => void = () => undefined;
    let alsMp3: () => void = () => undefined;
    const loeschen = async (): Promise<void> => {
      const ok = await confirmSheet({
        title: `Datei „${data.item.title}" löschen?`,
        confirmLabel: "Löschen",
        danger: true,
      });
      if (!ok) return;
      try {
        await deleteProjectFile(projectId, fileId);
        closeTopmost();
        onChange();
      } catch (err) {
        window.alert(
          `Löschen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    };
    const menuItems: Array<{ label: string; onSelect: () => void }> = [];
    if (!data.binary) {
      menuItems.push(
        { label: "Bearbeiten", onSelect: () => bearbeiten() },
        { label: "Als MP3 speichern", onSelect: () => alsMp3() },
      );
    }
    menuItems.push({ label: "Löschen", onSelect: () => void loeschen() });

    const titelJetzt = (): string =>
      anzeigeTitel(data.item.title, data.binary ? null : aktuellerText);
    const pageHead = buildPageHeader(titelJetzt(), () => closeTopmost(), {
      reopenMenu: false,
      subtitle: untertitel(),
      menuItems,
    });
    sheet.setAttribute("aria-label", titelJetzt());
    // Die Pille traegt den Projektnamen, nie den Dateinamen — hier kann der
    // Titel nie „im Ort" stehen. Sicher ist sicher.
    pageHead.classList.remove("page-head--ort-in-pille");
    headWrap.replaceChildren(pageHead, laufZeile);
    const title = pageHead.querySelector<HTMLElement>(".hd-title");
    const meta = pageHead.querySelector<HTMLElement>(".hd-subtitle");

    if (data.binary) {
      // Sprint D: Inline-Preview je nach Mime — PDF im iframe, Bild als
      // <img>, Audio mit <audio controls>. Sonst Download-Link.
      const rawUrl = `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}/raw`;
      const mime = data.item.mime ?? "";
      const wrap = document.createElement("div");
      wrap.className = "project-file-binary-preview";
      if (mime === "application/pdf") {
        const iframe = document.createElement("iframe");
        iframe.src = rawUrl;
        iframe.className = "project-file-pdf";
        iframe.title = data.item.title;
        wrap.appendChild(iframe);
      } else if (mime.startsWith("image/")) {
        const img = document.createElement("img");
        img.src = rawUrl;
        img.alt = data.item.title;
        img.className = "project-file-image";
        wrap.appendChild(img);
      } else if (mime.startsWith("audio/")) {
        const audio = document.createElement("audio");
        audio.controls = true;
        audio.src = rawUrl;
        audio.className = "project-file-audio";
        // 02.10.2026: beliebig lange Projektdateien — das Native erfährt, dass die Seite spielt.
        beobachteElement(audio);
        wrap.appendChild(audio);
      } else {
        const a = document.createElement("a");
        a.href = rawUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.appendChild(icon("download", { size: 14 }));
        a.appendChild(
          document.createTextNode(` ${data.item.title} herunterladen`),
        );
        a.className = "project-file-download";
        wrap.appendChild(a);
      }
      body.appendChild(wrap);
      return;
    }

    // Pre.974/975: Vorlese-Player ÜBER dem Text. Liegt eine gekürzte
    // HÖRFASSUNG vor (Essenz, beim Einspeisen erzeugt — ohne PDF-Müll), liest
    // der Player diese statt des rohen Volltexts; ein Umschalter erlaubt den
    // Originaltext. Der jeweilige Text wird in Abschnitte zerlegt
    // (Kapitel/Längen-Pakete): kurz = ein Abschnitt, lang = Kapitel-Hörbuch
    // (Vor/Zurück, Auto-Weiter, Positions-Resume pro Modus).
    const narr = data.narration ?? null;
    // 23.09.2026, Ticket 2285: die Wahl steht in shared/ — der Server wärmt
    // beim Öffnen denselben Text vor (server/projects.ts).
    const hoerBody = hoerfassungText(narr);
    const hasHoer = hoerBody.length > 0;
    let mode: "hoer" | "orig" = hasHoer ? "hoer" : "orig";

    const playerSlot = document.createElement("div");
    body.appendChild(playerSlot);
    // Die Hoerfassungs-Zeile steht UNTER dem Spieler — sie erklaert ihn, sie
    // kuendigt ihn nicht an.
    const srcRow = document.createElement("div");
    srcRow.className = "narration-source-row";
    body.appendChild(srcRow);

    const mountPlayer = (): void => {
      // Pre.975: laufenden Player stoppen, BEVOR der DOM-Knoten ersetzt wird
      // (sonst spielt sein Audio weiter).
      activePlayer?.reset();
      activePlayer = null;
      playerSlot.replaceChildren();
      if (!playerBuilder) return;
      const text = mode === "hoer" ? hoerBody : aktuellerText;
      const sections = splitDocChapters(text);
      if (sections.length === 0) return;
      const player = playerBuilder({
        endpoint: `/api/projects/${encodeURIComponent(projectId)}/narrate`,
        getBody: (voiceId) => ({ text: sections[0]?.text ?? text, voiceId }),
        sectionable: true,
        resumeId: `file:${fileId}:${mode}`,
        resumeVersion: contentVersion(text),
      });
      player.setSections(sections);
      player.setReady(true);
      // Pre.979: IMMER den aktuellen Abschnitt vorwärmen — prefetch() wärmt nur
      // EINEN Abschnitt (nicht das ganze Buch), und genau das macht das Audio
      // beim Abspielen zu einem Cache-Hit → endliche Dauer → Seek + Positions-
      // anzeige funktionieren.
      player.prefetch();
      playerSlot.appendChild(player.el);
      activePlayer = player;
      if (opts.vorlesen) {
        // Nur beim ERSTEN Aufbau, nicht bei jedem Moduswechsel — sonst
        // startet ein Umschalten zwischen Hörfassung und Original von selbst.
        opts.vorlesen = false;
        player.el.scrollIntoView({ block: "nearest" });
        try {
          player.play?.();
        } catch {
          /* Der Browser darf ablehnen; der Spieler steht trotzdem da. */
        }
      }
    };

    const renderSrcRow = (): void => {
      srcRow.replaceChildren();
      if (hasHoer) {
        const label = document.createElement("span");
        label.className = "narration-source-label";
        label.textContent =
          mode === "hoer"
            ? "Vorlesen: Hörfassung (gekürzt)"
            : "Vorlesen: Originaltext (vollständig)";
        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "btn btn--ghost btn--sm";
        toggle.textContent =
          mode === "hoer" ? "Original vorlesen" : "Hörfassung vorlesen";
        toggle.addEventListener("click", () => {
          mode = mode === "hoer" ? "orig" : "hoer";
          renderSrcRow();
          mountPlayer();
        });
        srcRow.append(label, toggle);
      } else if (narr?.status === "pending") {
        const s = document.createElement("span");
        s.className = "narration-source-label";
        s.textContent =
          "Hörfassung wird erstellt … (beim nächsten Öffnen verfügbar)";
        srcRow.appendChild(s);
      } else {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn btn--ghost btn--sm";
        btn.textContent =
          narr?.status === "failed"
            ? "Hörfassung erneut erstellen"
            : "Gekürzte Hörfassung erstellen";
        btn.addEventListener("click", () => {
          btn.disabled = true;
          btn.textContent = "wird erstellt …";
          void narrateProjectFile(projectId, fileId)
            .then((r) => {
              srcRow.replaceChildren();
              const s = document.createElement("span");
              s.className = "narration-source-label";
              s.textContent = r.tooShort
                ? "Text ist kurz — wird direkt vorgelesen."
                : "Hörfassung wird erstellt … (beim nächsten Öffnen verfügbar)";
              srcRow.appendChild(s);
            })
            .catch(() => {
              btn.disabled = false;
              btn.textContent = "Fehlgeschlagen — nochmal";
            });
        });
        srcRow.appendChild(btn);
      }
    };

    renderSrcRow();
    mountPlayer();

    // Pre.980: Metadaten manuell bearbeiten (Titel + Autor) — sichtbar nur im
    // Edit-Modus, oberhalb des Texts.
    const metaEdit = document.createElement("div");
    metaEdit.className = "project-file-meta-edit";
    metaEdit.hidden = true;
    const titleEdit = document.createElement("input");
    titleEdit.type = "text";
    titleEdit.className = "project-file-meta-input";
    titleEdit.placeholder = "Titel";
    titleEdit.value = data.item.title;
    const authorEdit = document.createElement("input");
    authorEdit.type = "text";
    authorEdit.className = "project-file-meta-input";
    authorEdit.placeholder = "Autor / Quelle (leer = keiner)";
    authorEdit.value = data.item.author ?? "";
    metaEdit.append(titleEdit, authorEdit);
    body.appendChild(metaEdit);

    // Der Text als Lesetext (XSS-sicher via renderMarkdown, KEIN innerHTML) —
    // wie der Ueberblick auf der Projektseite.
    const view = document.createElement("div");
    view.className = "project-file-lese";
    renderMarkdown(view, leseTextOhneKopfzeile(data.item.title, aktuellerText));
    body.appendChild(view);

    const editor = document.createElement("textarea");
    editor.className = "project-file-editor";
    editor.value = aktuellerText;
    editor.hidden = true;
    body.appendChild(editor);

    const actions = document.createElement("div");
    actions.className = "project-file-actions";
    actions.hidden = true;
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "btn btn--primary";
    saveBtn.textContent = "Speichern";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn btn--ghost";
    cancelBtn.textContent = "Abbrechen";
    actions.append(saveBtn, cancelBtn);
    body.appendChild(actions);

    const setEditing = (on: boolean): void => {
      view.hidden = on;
      editor.hidden = !on;
      metaEdit.hidden = !on;
      actions.hidden = !on;
      // Der Fokus geht dorthin, wo es weitergeht: oben in die Felder — und
      // zurueck auf das ⋯, von dem das Bearbeiten ausging (nicht ins Leere).
      if (on) titleEdit.focus();
      else pageHead.querySelector<HTMLElement>(".page-head-more")?.focus();
    };
    bearbeiten = (): void => setEditing(true);
    cancelBtn.addEventListener("click", () => {
      editor.value = aktuellerText;
      titleEdit.value = data.item.title;
      authorEdit.value = data.item.author ?? "";
      setEditing(false);
    });
    saveBtn.addEventListener("click", async () => {
      try {
        const updated = await updateProjectFile(projectId, fileId, {
          content: editor.value,
          title: titleEdit.value.trim() || data.item.title,
          author: authorEdit.value.trim() || null,
        });
        aktuellerText = editor.value;
        renderMarkdown(view, leseTextOhneKopfzeile(updated.title, aktuellerText));
        // lokalen Stand mitziehen, damit Abbrechen danach korrekt zurücksetzt
        data.item.title = updated.title;
        data.item.author = updated.author ?? null;
        data.item.sizeBytes = updated.sizeBytes;
        if (title) title.textContent = titelJetzt();
        if (meta) meta.textContent = untertitel();
        sheet.setAttribute("aria-label", titelJetzt());
        pageHead.setAttribute("aria-label", titelJetzt());
        setEditing(false);
        // Der Spieler liest den neuen Text — nicht den, der vor dem Speichern
        // galt.
        if (mode === "orig") mountPlayer();
        onChange();
      } catch (err) {
        window.alert(
          `Speichern fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });

    // 2026-07-13 (Martin): Skript als MP3 vertonen + dauerhaft speichern
    // (native Stimme, z.B. Spanisch zum Lernen). Erscheint danach als
    // Audiodatei im Projekt.
    let mp3Laeuft = false;
    alsMp3 = (): void => {
      if (mp3Laeuft) {
        // Zweiter Tipp waehrend des Laufs: nicht still schlucken — die Zeile
        // noch einmal zeigen, damit klar ist, dass es schon laeuft.
        melde("Erzeuge MP3 … läuft schon, einen Moment.");
        return;
      }
      void (async () => {
        const voice = await selectSheet({
          title: "Als MP3 speichern",
          message: "In welcher Stimme soll der Text vertont werden?",
          options: [
            { value: "es-ES-Chirp3-HD-Aoede", label: "Spanisch (muttersprachlich)" },
            { value: "de-DE-Chirp3-HD-Aoede", label: "Deutsch" },
          ],
        });
        if (!voice) return;
        mp3Laeuft = true;
        melde("Erzeuge MP3 … das kann eine Weile dauern.");
        try {
          await saveProjectFileAsMp3(projectId, fileId, {
            voiceId: voice,
            provider: "google",
            title: `${data.item.title} — Audio`,
          });
          melde("MP3 gespeichert — liegt jetzt im Projekt.");
          onChange();
          window.setTimeout(() => melde(null), 4000);
        } catch (err) {
          melde(null);
          window.alert(
            `MP3 fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
          );
        } finally {
          mp3Laeuft = false;
        }
      })();
    };
  } catch (err) {
    body.textContent = `Laden fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
  }
}
